import { PartialBrief } from '../Types/Brief'

export const collectionMapper: Record<string, string> = {
  '22': 'Weddings',
  '23': 'Events',
}

type BudgetGuidance = {
  basic: number
  average: number
  highEnd: number
}
type BudgetGuidanceSetting =
  | {
      [eventType: `${number}`]: BudgetGuidance
    }
  | {
      reference: string
      multiplier: number
    }
const budgetGuidanceByLocation: Record<
  string, // City, falls back to country if not found
  BudgetGuidanceSetting
> = {
  london: {
    '26': {
      basic: 40.0,
      average: 75.0,
      highEnd: 150.0,
    },
    '24': {
      basic: 25.0,
      average: 50.0,
      highEnd: 75.0,
    },
    '18': {
      basic: 25.0,
      average: 50.0,
      highEnd: 75.0,
    },
    '1': {
      basic: 20.0,
      average: 50.0,
      highEnd: 125.0,
    },
    '25': {
      basic: 25.0,
      average: 50.0,
      highEnd: 75.0,
    },
    '2': {
      basic: 50.0,
      average: 75.0,
      highEnd: 125.0,
    },
    '23': {
      basic: 50.0,
      average: 75.0,
      highEnd: 125.0,
    },
    '27': {
      basic: 25.0,
      average: 50.0,
      highEnd: 75.0,
    },
    '3': {
      basic: 15.0,
      average: 40.0,
      highEnd: 75.0,
    },
    '22': {
      basic: 80.0,
      average: 100.0,
      highEnd: 112.5,
    },
  },
  AU: {
    '26': {
      // Conference
      basic: 80.0,
      average: 200.0,
      highEnd: 300.0,
    },
    '24': {
      // Meeting/Workshop
      basic: 50.0,
      average: 85.0,
      highEnd: 110.0,
    },
    '18': {
      // Networking
      basic: 85.0,
      average: 110.0,
      highEnd: 250.0,
    },
    '1': {
      // Party
      basic: 50.0,
      average: 100.0,
      highEnd: 250.0,
    },
    '25': {
      // Presentation
      basic: 50,
      average: 85,
      highEnd: 110,
    },
    '2': {
      // Private Dining
      basic: 100,
      average: 150,
      highEnd: 300,
    },
    '23': {
      // Something Else
      basic: 50,
      average: 100,
      highEnd: 150.0,
    },
    '27': {
      // Studio
      basic: 40,
      average: 60,
      highEnd: 100,
    },
    '3': {
      // Team Activity
      basic: 30.0,
      average: 80.0,
      highEnd: 200.0,
    },
    '22': {
      // Wedding
      basic: 100.0,
      average: 250.0,
      highEnd: 400.0,
    },
  },
  US: {
    '26': {
      // Conference
      basic: 50.0,
      average: 450.0,
      highEnd: 1200.0,
    },
    '24': {
      // Meeting/Workshop
      basic: 50.0,
      average: 375.0,
      highEnd: 750.0,
    },
    '18': {
      // Networking
      basic: 50.0,
      average: 300.0,
      highEnd: 800.0,
    },
    '1': {
      // Party
      basic: 30.0,
      average: 375.0,
      highEnd: 1000.0,
    },
    '25': {
      // Presentation
      basic: 50.0,
      average: 375.0,
      highEnd: 1000.0,
    },
    '2': {
      // Private Dining
      basic: 30.0,
      average: 250.0,
      highEnd: 500.0,
    },
    '23': {
      // Something else
      basic: 100.0,
      average: 375.0,
      highEnd: 1000.0,
    },
    '27': {
      // Studio
      basic: 100.0,
      average: 375.0,
      highEnd: 1000.0,
    },
    '3': {
      // Team Activity
      basic: 30.0,
      average: 250.0,
      highEnd: 700.0,
    },
    '22': {
      // Wedding
      basic: 150.0,
      average: 525.0,
      highEnd: 1500.0,
    },
  },
  manchester: {
    reference: 'london',
    multiplier: 0.9,
  },
  GB: {
    reference: 'london',
    multiplier: 0.7,
  },
} as const

type GetBudgetGuidanceArgs = Pick<PartialBrief, 'leadFeedCity' | 'collection' | 'country'>

function isNumeric(value: string): value is `${number}` {
  return /^\d+$/.test(value)
}

export default function getBudgetGuidance({
  leadFeedCity,
  collection,
  country,
}: GetBudgetGuidanceArgs): BudgetGuidance | undefined {
  if (!leadFeedCity || !country || !collection) {
    return undefined
  }
  if (!isNumeric(collection)) {
    throw new Error(`Collection ${collection} is not a number`)
  }

  const city = leadFeedCity.toLowerCase()
  const guidance = budgetGuidanceByLocation[city] ?? budgetGuidanceByLocation[country]
  if (guidance && 'reference' in guidance) {
    const referenceGuidance = getBudgetGuidance({
      leadFeedCity: guidance.reference,
      collection,
      country,
    })
    if (!referenceGuidance) {
      throw new Error(`Reference guidance not found for ${guidance.reference}`)
    }
    const { multiplier } = guidance
    return {
      basic: referenceGuidance.basic * multiplier,
      average: referenceGuidance.average * multiplier,
      highEnd: referenceGuidance.highEnd * multiplier,
    }
  }
  const eventGuidance = guidance ? guidance[collection] : undefined
  return eventGuidance
}
